'use client'

import { cn } from '@/lib/tailwind'
import type { MediaKind } from '@/entries'
import { ENTRY_PRESENTATION } from '@/entries'
import {
  truncateText,
  MAX_TITLE_LENGTH,
  MAX_SUBTITLE_LENGTH,
  MAX_GENRE_LENGTH,
} from './utils'

interface MediaMetadataProps {
  type: MediaKind
  title: string
  subtitle?: string
  genres?: string[]
  description?: string
  topics?: string[]
}

/**
 * Displays metadata for media preview (title, subtitle, genres, description)
 */
export function MediaMetadata({
  type,
  title,
  subtitle,
  genres = [],
  description,
  topics = [],
}: MediaMetadataProps) {
  // `type` is a `MediaKind`, a subset of `EntryType`, so the record is total over it and
  // the old `|| …movie` fallbacks were unreachable (#390).
  const colors = ENTRY_PRESENTATION[type]
  const Icon = colors.icon

  const showGenres = genres && genres.length > 0

  // For books: show topics as a readable summary when no description
  const bookTopicsSummary =
    type === 'book' && !description && topics.length
      ? topics.slice(0, 5).join(' • ')
      : null

  return (
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4
            className="truncate text-sm font-medium"
            title={title.length > MAX_TITLE_LENGTH ? title : undefined}
          >
            {truncateText(title, MAX_TITLE_LENGTH)}
          </h4>
          {subtitle && (
            <p
              className="text-muted-foreground truncate text-xs"
              title={
                subtitle.length > MAX_SUBTITLE_LENGTH ? subtitle : undefined
              }
            >
              {truncateText(subtitle, MAX_SUBTITLE_LENGTH)}
            </p>
          )}
        </div>

        {/* Type badge */}
        <span
          className={cn(
            'flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
            colors.combined,
          )}
        >
          <Icon className="h-3 w-3" weight="fill" />
          {colors.label}
        </span>
      </div>

      {/* Genres (for artists, labels, tracks, releases, movies) */}
      {showGenres && (
        <div className="flex flex-wrap gap-1 overflow-hidden">
          {genres.slice(0, 4).map((genre, index) => (
            <span
              key={`${genre}-${index}`}
              className="bg-muted text-muted-foreground max-w-[120px] truncate rounded px-1.5 py-0.5 text-[10px]"
              title={genre.length > MAX_GENRE_LENGTH ? genre : undefined}
            >
              {truncateText(genre, MAX_GENRE_LENGTH)}
            </span>
          ))}
          {genres.length > 4 && (
            <span className="text-muted-foreground text-[10px]">
              +{genres.length - 4}
            </span>
          )}
        </div>
      )}

      {/* Description (for artists, books with description) */}
      {description && (
        <p className="text-muted-foreground line-clamp-2 text-xs">
          {description}
        </p>
      )}

      {/* Book topics summary (when no description available) */}
      {bookTopicsSummary && (
        <p className="text-muted-foreground text-xs">{bookTopicsSummary}</p>
      )}

      {/* Book topics as badges (when description exists but we also have topics) */}
      {type === 'book' && description && topics && topics.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {topics.slice(0, 3).map((topic) => (
            <span
              key={topic}
              className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px]"
            >
              {topic}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
